import { data, redirect, useLoaderData, type LoaderFunctionArgs, type MetaFunction } from 'react-router'
import SearchView from '@/site/components/SearchView'
import { requestOptions } from '@/lib/http'
import { facetHref, formatCount, splitFacetValue } from '@/site/lib/format'
import {
  isSearchVariation,
  knownSearchParams,
  legacyFilterParams,
  listingCanonicalPath,
  loadSearchResults,
  searchStateFromParams,
  singleFacetFilter,
  type FacetFilter,
} from '@/site/lib/searchPage'
import { DEFAULT_DESCRIPTION, errorMeta, NOINDEX_FOLLOW, pageQuery, pageSuffix, seoMeta, SITE_NAME, siteUrlOf } from '@/site/lib/seo'
import { loadCollections } from '@/site/lib/siteData'

/** "Fabric-Cut" (URL) -> "Fabric Cut" (facet). */
const facetName = (filter: string) => filter.replace(/[-_]/g, ' ')

/** "Jenny Doan Quilt Tutorials", "Layer Cake Quilt Tutorials". */
const landingTitle = ({ facet, value }: FacetFilter) => `${splitFacetValue(facet, value).name} Quilt Tutorials`

function landingDescription({ facet, value }: FacetFilter, total: number): string {
  const name = splitFacetValue(facet, value).name
  const count = formatCount(total)
  return facet === 'Instructors'
    ? `Watch ${count} free quilting tutorials with ${name}: step-by-step quilt videos you can follow on desktop or mobile.`
    : `Free ${name} quilt tutorials: ${count} step-by-step quilting videos to watch anywhere, on desktop or mobile.`
}

/**
 * /tutorials (every tutorial) and /:filter/:value (one facet value, e.g. /Skill-Level/Beginner).
 *
 * One-filter listings are landing pages (the path form above, and v1's /tutorials?Instructors=Jenny+Doan): they
 * are indexed, titled after the value, and canonical to the value's collection page when one exists. Query params
 * the search doesn't know (?_ga=, v1's ?Type=) are ignored; old storefront ?filter.x= links redirect. A 404, not a
 * page Google calls a soft 404: a path facet that doesn't exist (/fintech/x would list every tutorial), a landing
 * page without tutorials, a page past the last.
 */
export async function loader({ request, params }: LoaderFunctionArgs) {
  const url = new URL(request.url)
  const route = { filter: params.filter, value: params.value }
  const [results, collections] = await Promise.all([loadSearchResults(url.searchParams, route, requestOptions(request)), loadCollections()])

  const routeFacet = route.filter && route.value ? { facet: facetName(route.filter), value: route.value } : null

  // An old storefront filter link (?filter.skill_level=Beginner) -> the same filter here, at its collection page if any.
  const legacy = routeFacet ? null : legacyFilterParams(url.searchParams, results.facets)
  if (legacy) {
    const single = singleFacetFilter(legacy)
    throw redirect(single ? facetHref(single.facet, single.value, collections) : `/tutorials?${legacy}`, 301)
  }

  const searchParams = knownSearchParams(url.searchParams, results.facets)
  // A landing page: the path facet on its own, or /tutorials with exactly one filter.
  const landing = routeFacet ? (isSearchVariation(searchParams) ? null : routeFacet) : singleFacetFilter(searchParams)

  const unknownFacet = routeFacet && results.facets.length > 0 && !results.facets.some((f) => f.field === routeFacet.facet)
  const pastLastPage = searchStateFromParams(searchParams).page > Math.max(1, results.totalPages)
  if (unknownFacet || (landing && results.totalResults === 0) || pastLastPage) {
    throw data(null, { status: 404 })
  }

  let canonicalPath: string | null = null
  if (landing) {
    const href = facetHref(landing.facet, landing.value, collections)
    const hasCollection = !href.startsWith('/tutorials?')
    canonicalPath = hasCollection || !routeFacet ? href : url.pathname
  }

  return { results, facet: routeFacet, landing, canonicalPath }
}

export const meta: MetaFunction<typeof loader> = ({ loaderData, location, matches, error }) => {
  if (!loaderData || error) return errorMeta(404)

  const { landing, canonicalPath, results } = loaderData
  const params = knownSearchParams(new URLSearchParams(location.search), results.facets)
  const { query, page } = searchStateFromParams(params)

  if (landing && canonicalPath) {
    const pagedCanonical = canonicalPath.includes('?')
      ? `${canonicalPath}${page > 1 ? `&page=${page}` : ''}`
      : `${canonicalPath}${pageQuery(page)}`
    return seoMeta({
      title: `${landingTitle(landing)}${pageSuffix(page)} | ${SITE_NAME}`,
      description: landingDescription(landing, results.totalResults),
      siteUrl: siteUrlOf(matches),
      path: pagedCanonical,
    })
  }

  const title = query ? `${query} | ${SITE_NAME}` : `Free Quilting Tutorials & Videos${pageSuffix(page)} | ${SITE_NAME}`
  return seoMeta({
    title,
    description: DEFAULT_DESCRIPTION,
    siteUrl: siteUrlOf(matches),
    path: listingCanonicalPath(location.pathname, params),
    robots: isSearchVariation(params) ? NOINDEX_FOLLOW : undefined,
  })
}

export default function Search() {
  const { results, facet, landing } = useLoaderData<typeof loader>()
  const heading = landing ? landingTitle(landing) : facet ? landingTitle(facet) : undefined
  return <SearchView results={results} heading={heading} />
}
